import axios, { AxiosError } from 'axios';

const api = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL || 'http://localhost:1700',
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('micus_admin_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

let unauthorizedHandler: ((message: string) => void) | null = null;

/** Called once from main.tsx so the store can sign out when the API rejects the session. */
export function onUnauthorized(handler: (message: string) => void) {
  unauthorizedHandler = handler;
}

api.interceptors.response.use(
  (res) => res,
  (err: AxiosError<{ message?: string }>) => {
    const isLogin = err.config?.url?.includes('/auth/login');
    if (err.response?.status === 401 && !isLogin && unauthorizedHandler) {
      unauthorizedHandler(err.response.data?.message || 'Your session has expired. Sign in again.');
    }
    return Promise.reject(err);
  },
);

export function apiError(e: unknown, fallback: string): string {
  return (e as { response?: { data?: { message?: string } } })?.response?.data?.message || fallback;
}

/** Protected files (invoice PDFs) need the bearer token, so fetch as a blob and open an object URL. */
export async function openProtectedFile(url: string): Promise<void> {
  const tab = window.open('', '_blank');
  try {
    const { data, headers } = await api.get<Blob>(url, { responseType: 'blob', timeout: 120000 });
    const blob = new Blob([data], { type: String(headers['content-type'] || data.type || 'application/octet-stream') });
    const objectUrl = URL.createObjectURL(blob);
    if (tab) tab.location.href = objectUrl;
    else window.location.assign(objectUrl);
    setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
  } catch (e) {
    tab?.close();
    throw e;
  }
}

export default api;
